import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { useLexicalIsTextContentEmpty } from '@lexical/react/useLexicalIsTextContentEmpty';
import { EditIcon } from '@medly-components/icons';
import { memo, useContext, useEffect, useRef, useState, type FC } from 'react';

import { useLexicalIsFocused } from 'hooks';

import { Accordion } from '@medly-components/core';
import { useOuterClickNotifier } from '@medly-components/utils';
import { EditorButton, EditorWrapper, InnerEditor } from './Editor.styled';
import AutoLinkPlugin from './plugins/AutoLinkPlugin';
import ToolbarPlugin from './plugins/ToolbarPlugin';
import { EditorProps } from './types';

const Placeholder = () => <div className="editor-placeholder">Start typing your content here . . .</div>;

export const Editor: FC<EditorProps> = memo(({ id, onSave }) => {
    const ref = useRef(null);
    const [open] = useContext(Accordion.Context);
    const [editor] = useLexicalComposerContext();
    const [isEditable, setEditable] = useState(false);
    const [isClicked, setIsClicked] = useState(false);
    const [isMouseMove, setIsMouseMove] = useState(false);
    const isFocused = useLexicalIsFocused();
    const isEmpty = useLexicalIsTextContentEmpty(editor, true) && editor._editorState._nodeMap.size < 3;

    const handleEdit = () => setEditable(true);
    const handleSave = () => {
        const content = isEmpty ? '{}' : JSON.stringify(editor.getEditorState().toJSON());
        const isEditable = content === '{}';
        onSave ? onSave(content, () => setEditable(isEditable)) : setEditable(isEditable);
    };

    useEffect(() => {
        if (open && onSave) {
            setEditable(isEmpty);
        } else {
            setEditable(false);
        }
    }, [open, onSave]);

    useEffect(() => {
        editor.setEditable(isEditable);
    }, [isEditable]);

    useOuterClickNotifier(() => {
        open && isEditable && !(isClicked && isMouseMove) && handleSave();
        setIsMouseMove(false);
        setIsClicked(false);
    }, ref);

    if ((!open || !onSave) && isEmpty) return null;

    return (
        <>
            <EditorWrapper
                ref={ref}
                isEditable={isEditable}
                onMouseDown={() => setIsClicked(true)}
                onMouseMove={() => setIsMouseMove(true)}
                onMouseUp={() => {
                    setIsMouseMove(false);
                    setIsClicked(false);
                }}
            >
                {isEditable && <ToolbarPlugin />}
                <InnerEditor>
                    <RichTextPlugin
                        placeholder={<Placeholder />}
                        ErrorBoundary={LexicalErrorBoundary}
                        contentEditable={
                            <ContentEditable
                                id={id}
                                className="editor-input"
                                spellCheck={false}
                                autoCorrect="off"
                                autoComplete="off"
                                autoCapitalize="off"
                                contentEditable={isEditable}
                            />
                        }
                    />
                    {isEditable && (
                        <>
                            <HistoryPlugin />
                            <ListPlugin />
                            <CheckListPlugin />
                            <LinkPlugin />
                            <AutoLinkPlugin />
                        </>
                    )}
                </InnerEditor>
            </EditorWrapper>
            {onSave &&
                open &&
                (isEditable || isEmpty ? (
                    <EditorButton disabled={!isFocused && isEmpty}>Save</EditorButton>
                ) : (
                    <EditorButton id="notes-edit-btn" onClick={handleEdit}>
                        <EditIcon />
                        Edit
                    </EditorButton>
                ))}
        </>
    );
});
